import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { electionStorageKey, pickElection, supporterElectionOptions } from '../components/pages/supporter/utils';
import { getUserData } from '../constants/global';
import type { ISupporterElection, ISupporterElectionSummary, ISupporterProfile } from '../interface/supporter';
import type { IIncident } from '../interface/vesr';
import { getSupporterMe, getSupporterSummary, type SupporterOutcome } from '../lib/supporter-api';
import { parseSupporterClaims } from '../lib/supporter-claims';
import { endSupporterSession } from '../lib/supporter-session';
import { listIncidents } from '../lib/vesr-api';

/** Fired by the report panel once VESR has the report, so the home's list catches up at once. */
export const SUPPORTER_REPORT_FILED = 'ielect:supporter-report-filed';

export type LoadState = 'loading' | 'ready' | 'refused' | 'unavailable';

export interface ISupporterHome {
	profileState: LoadState;
	profile: ISupporterProfile | null;
	/** api.core's own words when it refused (403/404). */
	profileMessage: string | null;
	elections: ISupporterElection[];
	selectedId: number | null;
	selected: ISupporterElection | null;
	select: (electionId: number) => void;
	summaryState: LoadState;
	summary: ISupporterElectionSummary | null;
	summaryMessage: string | null;
	reportsState: LoadState;
	reports: IIncident[];
	reportsMessage: string | null;
	retry: () => void;
	reloadReports: () => void;
}

const readRemembered = (key: string): number | null => {
	try {
		const n = Number(localStorage.getItem(key));
		return Number.isInteger(n) && n > 0 ? n : null;
	} catch {
		return null;
	}
};

const remember = (key: string, id: number) => {
	try {
		localStorage.setItem(key, String(id));
	} catch {
		// Storage unavailable — the choice lasts for this visit only.
	}
};

/**
 * Everything the Independent Supporter home reads: who they follow, the elections they may switch
 * between, the party's coverage in the selected one, and their own reports in it.
 *
 * <p>A read whose token cannot be refreshed ends the session (see supporter-session.ts) instead of
 * leaving a home of failed cards. The incident list is the supporter's own by construction:
 * api.vesr scopes a supporter to the reports they filed, in their party.</p>
 */
export const useSupporterHome = (): ISupporterHome => {
	const user = getUserData().user as unknown as Record<string, unknown> | undefined;
	const userId = user?.UserId as string | undefined;
	// The token is decoded afresh on every render, so key the memo on the claim's value, not its identity.
	const electionClaim = JSON.stringify(user?.election ?? null);
	const claimIds = useMemo(() => parseSupporterClaims({ election: JSON.parse(electionClaim) }).electionIds, [electionClaim]);
	const storageKey = electionStorageKey(userId);

	const [profileState, setProfileState] = useState<LoadState>('loading');
	const [profile, setProfile] = useState<ISupporterProfile | null>(null);
	const [profileMessage, setProfileMessage] = useState<string | null>(null);
	const [chosen, setChosen] = useState<number | null>(() => readRemembered(storageKey));

	const [summaryState, setSummaryState] = useState<LoadState>('loading');
	const [summary, setSummary] = useState<ISupporterElectionSummary | null>(null);
	const [summaryMessage, setSummaryMessage] = useState<string | null>(null);

	const [reportsState, setReportsState] = useState<LoadState>('loading');
	const [reports, setReports] = useState<IIncident[]>([]);
	const [reportsMessage, setReportsMessage] = useState<string | null>(null);

	const [attempt, setAttempt] = useState(0);

	/** Ends the session on a refused token; true when the caller should stop. */
	const ended = <T>(out: SupporterOutcome<T>) => {
		if (out.kind !== 'ended') return false;
		endSupporterSession(out.message);
		return true;
	};

	useEffect(() => {
		let live = true;
		setProfileState('loading');
		void getSupporterMe().then((out) => {
			if (!live || ended(out)) return;
			if (out.kind === 'ok') {
				setProfile(out.data);
				setProfileMessage(null);
				setProfileState('ready');
			} else if (out.kind === 'refused') {
				setProfileMessage(out.message);
				setProfileState('refused');
			} else {
				setProfileState('unavailable');
			}
		});
		return () => {
			live = false;
		};
	}, [attempt]);

	const elections = useMemo(() => supporterElectionOptions(profile, claimIds), [profile, claimIds]);
	const selectedId = pickElection(elections, chosen);
	const selected = elections.find((e) => e.electionId === selectedId) ?? null;

	const select = useCallback(
		(electionId: number) => {
			setChosen(electionId);
			remember(storageKey, electionId);
		},
		[storageKey]
	);

	useEffect(() => {
		if (!selectedId) return undefined;
		let live = true;
		setSummaryState('loading');
		setSummary(null);
		void getSupporterSummary(selectedId).then((out) => {
			if (!live || ended(out)) return;
			if (out.kind === 'ok') {
				setSummary(out.data);
				setSummaryMessage(null);
				setSummaryState('ready');
			} else if (out.kind === 'refused') {
				setSummaryMessage(out.message);
				setSummaryState('refused');
			} else {
				setSummaryState('unavailable');
			}
		});
		return () => {
			live = false;
		};
	}, [selectedId, attempt]);

	// Reports are fetched per election; a newer request supersedes an older one still in flight.
	const reportsSeq = useRef(0);
	const loadReports = useCallback((electionId: number | null, silent = false) => {
		if (!electionId) return;
		reportsSeq.current += 1;
		const seq = reportsSeq.current;
		if (!silent) setReportsState('loading');
		void listIncidents({ electionId: String(electionId), pageSize: 50 }).then((out) => {
			if (seq !== reportsSeq.current) return;
			if (!out.reachable) {
				if (!silent) setReportsState('unavailable');
				return;
			}
			if (out.httpStatus >= 200 && out.httpStatus < 300 && out.body) {
				const mine = [...(out.body.data ?? [])].sort(
					(a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
				);
				setReports(mine);
				setReportsMessage(null);
				setReportsState('ready');
				return;
			}
			if (out.httpStatus >= 400 && out.httpStatus < 500) {
				setReportsMessage(out.errorMessage ?? null);
				setReportsState('refused');
				return;
			}
			if (!silent) setReportsState('unavailable');
		});
	}, []);

	useEffect(() => {
		loadReports(selectedId);
	}, [selectedId, attempt, loadReports]);

	// A filed report, and coming back to the app, refresh the list. No interval: the incidents
	// controller is rate-limited per IP, and a supporter's reports change only when the room acts.
	useEffect(() => {
		const onFiled = () => loadReports(selectedId, true);
		const onVisible = () => {
			if (document.visibilityState === 'visible') loadReports(selectedId, true);
		};
		window.addEventListener(SUPPORTER_REPORT_FILED, onFiled);
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			window.removeEventListener(SUPPORTER_REPORT_FILED, onFiled);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, [selectedId, loadReports]);

	return {
		profileState,
		profile,
		profileMessage,
		elections,
		selectedId,
		selected,
		select,
		summaryState,
		summary,
		summaryMessage,
		reportsState,
		reports,
		reportsMessage,
		retry: () => setAttempt((a) => a + 1),
		reloadReports: () => loadReports(selectedId),
	};
};
